import { CommonModule } from "@angular/common";
import { ChangeDetectionStrategy, Component, ElementRef, OnDestroy } from "@angular/core";
import Player, { Options, TimeEvent } from "@vimeo/player";
import { ExternalLinkVideoPlayerComponent } from "../external-link-video-player";
import { BehaviorSubject } from "rxjs";
import { provideClearlineCommonTranslocoScope } from "../../../utils";
import { TranslocoModule } from "@ngneat/transloco";
import { CfsTemplateError, CfsTemplateErrorData, CfsTemplateMediaConfig } from "../../../models";
import { mediaReadyDelay } from "../../cfs-template";
import { RelativeLoaderComponent } from "../../relative-loader";
import {
  VideoPlayerErrorMessages,
  VideoPlayerErrorTitles,
  VideoPlayerErrorTypes
} from "../../cfs-template/video-template/video-player-error-messages";
import {
  CfsTemplateErrorDataFields,
  generateCfsTemplateError,
  generateVimeoPlayerErrorMessage
} from "../../cfs-template/cfs-template-errors";

export const vimeoUrlPattern =
  /^(?:https?:\/\/)?(?:www\.)?vimeo\.com\/(?:channels\/(?:\w+\/)?|groups\/(?:[^\/]+\/)?videos\/|album\/(?:\d+\/)?video\/|)(\d+)(?:[?&]\S*)?$/;

@Component({
  selector: "lib-vimeo-player",
  templateUrl: "./vimeo-player.component.html",
  styleUrls: ["./vimeo-player.component.scss"],
  standalone: true,
  imports: [CommonModule, TranslocoModule, RelativeLoaderComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [provideClearlineCommonTranslocoScope()]
})
export class VimeoPlayerComponent extends ExternalLinkVideoPlayerComponent implements OnDestroy {
  private isUrlValidationInProgress = false;
  protected lastErrorName = "";

  protected override player: Player | null = null;
  protected videoId = 0;
  protected isVideoValid$ = new BehaviorSubject<boolean>(true);
  protected urlPattern = vimeoUrlPattern;

  constructor(private element: ElementRef) {
    super();
  }

  async ngOnDestroy() {
    this.isDestroyInProgress = true;

    await this.resetPlayer();
  }

  play(): void {
    if (this.isPlayerActive()) {
      this.isVideoPlaying().then((isPlaying) => {
        if (!isPlaying && this.player?.play) {
          this.player
            ?.play()
            .then(() => console.log("Vimeo video played")) // todo: remove logs after testing
            .catch((error) => {
              this.createAndEmitError(VideoPlayerErrorTypes.Play, error);
              void this.resetPlayer();
            });
        }
      });
    }
  }

  pause(): void {
    if (this.isPlayerActive() && this.player?.pause) {
      this.player?.pause().then(() => console.log("Vimeo video paused")); // todo: remove logs after testing
    }
  }

  stop(): void {
    if (this.isPlayerActive() && this.player?.setCurrentTime) {
      this.player?.setCurrentTime(0).then(() => {
        console.log("Video reset to 0"); // todo: remove logs after testing
        this.pause();
      });
    }
  }

  restart(): void {
    if (this.isPlayerActive() && this.player?.setCurrentTime) {
      this.player?.setCurrentTime(0).then(() => {
        this.play();
      });
    }
  }

  getVideoDuration(): Promise<number> {
    if (!this.isPlayerActive() || !this.player?.getDuration) {
      return Promise.resolve(0);
    }

    return this.player?.getDuration() || Promise.resolve(0);
  }

  getThumbnailUrl(): Promise<string> {
    if (!this.isPlayerActive() || !this.player?.getVideoId) {
      return Promise.resolve("");
    }

    return (
      this.player?.getVideoId().then((videoId) => {
        return videoId ? `https://vumbnail.com/${videoId}.jpg` : "";
      }) || Promise.resolve("")
    );
  }

  protected loadPlayer(autoplay = false): void {
    if (!this.videoId) {
      this.createAndEmitError(VideoPlayerErrorTypes.RequiredId, null, { field: CfsTemplateErrorDataFields.Id, value: `${this.videoId}` });
      void this.resetPlayer();
      return;
    }

    if (this.urlChecked) {
      if (!this.player) {
        this.player = this.getPlayer(autoplay);
      } else {
        this.player
          ?.loadVideo(this.videoId)
          .then(() => {})
          .catch((error: Error) => {
            this.createAndEmitError(VideoPlayerErrorTypes.Load, error);
            void this.resetPlayer();
          });
      }
    } else {
      this.isVideoUrlValid()
        .then((isAccessible) => {
          this.urlChecked = true;

          if (isAccessible) {
            this.isVideoValid$.next(true);
            this.loadPlayer(autoplay);
          } else {
            this.createAndEmitError(VideoPlayerErrorTypes.InvalidUrl);
            void this.resetPlayer();
          }
        })
        .catch((error: Error) => {
          this.urlChecked = true;
          this.createAndEmitError(VideoPlayerErrorTypes.InvalidUrl, error);
          void this.resetPlayer();
        });
    }
  }

  protected getVideoIdByUrl(url: string): number {
    const match = url.match(this.urlPattern) || [];

    return match.length ? parseInt(match[1], 10) : 0;
  }

  private generateErrorMessage(errorType: VideoPlayerErrorTypes | string, error: Error | null = null): string {
    const errorName = error?.name;
    const nameMessage = errorName ? `${errorName}. ` : "";
    const errorMessage = error?.message || "";
    const details = nameMessage || errorMessage ? `${nameMessage}${errorMessage}` : "";
    const content = details || generateVimeoPlayerErrorMessage(errorType) || VideoPlayerErrorMessages.Unknown;

    return `${VideoPlayerErrorTitles.Vimeo}: ${content}`;
  }

  private getPlayer(withPlaying = false): Player {
    const options: Options = this.generatePlayerOptions(this.videoId, this.mediaConfig);
    const player: Player = new Player(this.element.nativeElement, options);

    void player.pause();

    player.on("ended", () => {
      if (!this.mediaConfig.loop) {
        this.stop();
      }
    });

    player.on("pause", () => {
      console.log("Vimeo video paused"); // todo: remove logs after testing
    });

    player.on("loaded", (event: TimeEvent) => {
      console.log("Vimeo video loaded: ", event); // todo: remove logs after testing
      setTimeout(() => {
        this.loaded = true;
        this.ready.emit();

        if (!!this.mediaConfig.autoplay || withPlaying) {
          this.play();
        }
      }, mediaReadyDelay);
    });

    player.on("error", (error: Error) => {
      const restartPlayerErrorNames = ["PlaybackError", "PlayInterrupted", "AbortError"];
      const isRestartRequired: boolean = restartPlayerErrorNames.includes(error.name) && error.name !== this.lastErrorName;
      this.lastErrorName = error.name;

      this.createAndEmitError(error.name, error);

      if (isRestartRequired) {
        this.restartPlayer();
      }
    });

    return player;
  }

  private createAndEmitError(
    errorType: VideoPlayerErrorTypes | string,
    error: Error | null = null,
    errorData: CfsTemplateErrorData | null = null
  ): void {
    const errorMessage = this.generateErrorMessage(errorType, error);
    errorData = errorData || { field: CfsTemplateErrorDataFields.Url, value: this.videoUrl };
    const templateError: CfsTemplateError = generateCfsTemplateError(errorType, errorMessage, errorData);

    this.setError(templateError);
    this.isVideoValid$.next(false);
  }

  private generatePlayerOptions(videoId: number, mediaConfig: CfsTemplateMediaConfig): Options {
    const { muted = true, loop = false } = mediaConfig || {};

    return {
      id: videoId,
      muted,
      byline: false,
      controls: false,
      loop,
      autoplay: false,
      background: true,
      autopause: false,
      keyboard: false
    };
  }

  private restartPlayer(timeout = 300): void {
    this.resetPlayer().then(() => {
      setTimeout(() => {
        this.loadPlayer(true);
      }, timeout);
    });
  }

  private async isVideoUrlValid(): Promise<boolean> {
    if (this.isUrlValidationInProgress) {
      return Promise.resolve(false);
    }

    this.isUrlValidationInProgress = true;

    try {
      // todo: temporary solution, move checking video url validity to back end:
      const response = await fetch("https://vimeo.com/api/oembed.json?url=" + encodeURIComponent(this.videoUrl));
      return response?.status === 200;
    } finally {
      this.isUrlValidationInProgress = false;
    }
  }

  private async resetPlayer(): Promise<any> {
    if (this.player) {
      console.log("Vimeo player destroy"); // todo: remove logs after testing
      await this.player.destroy();
      this.player = null;
    }

    return Promise.resolve();
  }

  private isVideoPlaying(): Promise<boolean> {
    if (!this.player) {
      return Promise.resolve(false);
    }

    return Promise.all([this.player.getCurrentTime(), this.player.getPaused()]).then(([currentTime, paused]) => currentTime > 0 && !paused);
  }
}
